import React, { useCallback, useEffect, useState } from 'react';
import { momentService } from '../../../services/momentService';
import { Spinner } from '../../ui/Spinner';
import { Button } from '../../ui/Button';
import { Alert } from '../../ui/Alert';
import { PermanentDeleteModal } from './PermanentDeleteModal';
import { formatMomentDate } from '../../../utils/momentDates';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

export const TrashView = ({ onRestored }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      setItems(await momentService.listTrash());
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load Trash.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 3000);
  };

  const restore = async (m) => {
    setBusyId(m.id);
    setError('');
    try {
      await momentService.restoreMoment(m.id);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      await onRestored();
      flash('Moment restored to your timeline.');
    } catch (err) {
      setError(err.message || 'Could not restore this moment.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async () => {
    await momentService.deletePermanently(toDelete.id);
    setItems((prev) => prev.filter((x) => x.id !== toDelete.id));
    setToDelete(null);
    flash('Moment permanently deleted.');
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium text-slate-500">
        Deleted moments stay here for 30 days, then are permanently deleted.
      </p>
      {notice && <Alert variant="success" message={notice} />}
      {error && <Alert variant="error" message={error} />}
      {items.length === 0 ? (
        <div className="bg-white rounded-[2rem] border border-slate-200/60 border-dashed p-12 text-center shadow-sm">
          <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-slate-100">
            <SafeIcon icon={FiIcons.FiTrash2} className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2 tracking-tight">Trash is empty</h3>
          <p className="text-[15px] font-medium text-slate-500">Moments you delete will wait here so you can get them back.</p>
        </div>
      ) : items.map((m) => (
        <div key={m.id} className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm">
          <p className="text-xs font-medium text-slate-400">Happened {formatMomentDate(m.occurred_at)}</p>
          <h3 className="mt-1 text-lg font-bold text-slate-900 tracking-tight break-words">{m.title || 'Untitled moment'}</h3>
          {m.body && <p className="mt-1 text-[15px] text-slate-600 line-clamp-2 whitespace-pre-wrap break-words">{m.body}</p>}
          {(m.origin_label || m.people.length > 0 || m.attachment_count > 0) && (
            <p className="mt-2 text-xs font-semibold text-slate-500 break-words">
              {[
                m.origin_label,
                m.people.length > 0 && `With ${m.people.map((p) => p.full_name || 'Contact').join(', ')}`,
                m.attachment_count > 0 && `${m.attachment_count} attachment${m.attachment_count > 1 ? 's' : ''}`
              ].filter(Boolean).join(' · ')}
            </p>
          )}
          <p className="mt-3 text-xs font-medium text-amber-700">
            Deleted {formatMomentDate(m.deleted_at)} · permanently deleted on {formatMomentDate(m.purge_after)} ({daysLeft(m.purge_after)} days left)
          </p>
          <div className="mt-4 flex flex-wrap justify-end gap-3">
            <Button variant="secondary" onClick={() => setToDelete(m)} disabled={busyId === m.id}>Delete permanently</Button>
            <Button onClick={() => restore(m)} isLoading={busyId === m.id}>Restore</Button>
          </div>
        </div>
      ))}
      {toDelete && <PermanentDeleteModal moment={toDelete} onConfirm={remove} onClose={() => setToDelete(null)} />}
    </div>
  );
};